// Archive subpage (rendered at /transactions/archive). Same chapter grammar
// as TransactionListV2, but only shows archived statuses and adds a year
// filter at the top.

import React from 'react';
import PillTabRail from '../../ui/PillTabRail';
import StatusChapter from './v2/StatusChapter';
import TransactionViewTabs from './v2/TransactionViewTabs';
import ChapterTable from './v2/ChapterTable';
import ChapterRail from './v2/ChapterRail';
import BulkActionBar from './v2/BulkActionBar';
import { statusKindFor } from './v2/statusKinds';
import { useTransactionChapters } from '../../../hooks/v2/useTransactionChapters';
import { useChapterScrollSpy } from '../../../hooks/v2/useChapterScrollSpy';
import { deriveArchiveYear } from '../../../utils/archiveGrouping';
import { Search } from 'lucide-react';
import TransactionTypeFilter from './v2/TransactionTypeFilter';
import { matchesTransactionQuery } from './v2/flattenChaptersForVirtuoso';
import ContactNamesContext from './v2/ContactNamesContext';
import { useContactNamesForDeals } from './v2/useContactNamesForDeals';
// TAW-03 (41-09, D-17): reuse 41-06's picker and merge helper rather than
// building a second one (this plan's whole reason for existing is to make
// the already-built archive scope REACHABLE, not to reimplement it).
import TeamScopeFilter from './v2/TeamScopeFilter';
// From the pure-helpers module, NOT from ./TransactionListV2: this page is
// eager (AppRouter.js imports it directly), so importing a helper out of the
// lazy route component pulled that whole component into the initial bundle
// (BOOT-01).
import { mergeScopedTransactions } from './v2/transactionListHelpers';
import { ARCHIVE_YEAR_ALL } from '../../../utils/tawArchiveScope';

// Preserves the archive's scroll position across a navigation to a transaction
// form and back (the form back button does history-back). Module-scoped so it
// survives this component's unmount, mirroring TransactionListV2's
// savedVirtuosoState. Only written when the page is in its default shape.
let savedArchiveScrollTop = 0;

const TransactionsArchivePage = ({
    transactions = [],
    statuses = [],
    canViewFinancials = true,
    currentYear = new Date().getFullYear(),
    onEdit = () => {},
    onBack = () => {},
    onBulkMove = () => {},
    // TAW-03 (41-09, D-17): the scope-picker socket, all defaulted so every
    // existing caller/test renders byte-identically to today when these are
    // not passed (no chip row, no attribution, no notice, same year tabs —
    // D-25).
    teamTransactions = [],
    includeOwnTransactions = true,
    teamScope = 'own',
    childWorkspaces = [],
    isParentWorkspace = false,
    onScopeChange = () => {},
    // yearFilter deliberately has NO default -- undefined selects the
    // uncontrolled (internal-state) path below.
    yearFilter: yearFilterProp,
    onYearFilterChange,
    // 55-20: the workspace whose contacts name a contactId-only client.
    workspaceId = null,
}) => {
    // Controlled/uncontrolled year: kept as the internal fallback (today's
    // behavior) so every existing caller/test that doesn't pass yearFilter
    // stays byte-identical. App.js (via AppRouter.js) now supplies both
    // props, making this CONTROLLED for the real archive route.
    const [internalYearFilter, setInternalYearFilter] = React.useState(ARCHIVE_YEAR_ALL);
    const yearFilter = yearFilterProp === undefined ? internalYearFilter : yearFilterProp;
    const handleYearFilterChange = React.useCallback((value) => {
        if (onYearFilterChange) onYearFilterChange(value);
        else setInternalYearFilter(value);
    }, [onYearFilterChange]);
    const [chapterCollapseOverrides, setChapterCollapseOverrides] = React.useState(() => new Map());
    const [selectedIds, setSelectedIds] = React.useState(() => new Set());
    const [bulkBusy, setBulkBusy] = React.useState(false);
    const [bulkTargetStatus, setBulkTargetStatus] = React.useState('');

    // Search (150ms-debounced, mirroring TransactionListV2) + client-type quick
    // filter (Set of stored transactionType values; empty = show all).
    const [searchInput, setSearchInput] = React.useState('');
    const [searchQuery, setSearchQuery] = React.useState('');
    React.useEffect(() => {
        const timer = setTimeout(() => setSearchQuery(searchInput), 150);
        return () => clearTimeout(timer);
    }, [searchInput]);
    const [typeFilter, setTypeFilter] = React.useState(() => new Set());
    const toggleTypeFilter = React.useCallback((type) => {
        setTypeFilter(prev => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type); else next.add(type);
            return next;
        });
    }, []);

    // Scroll-position restore across navigation to a transaction form and back.
    // Capture only in the default shape — this component fully unmounts on
    // navigation, so its filter state resets to defaults on return; a scrollTop
    // captured from a filtered view wouldn't map onto the default remount.
    const scrollRef = React.useRef(null);
    const defaultShapeRef = React.useRef(true);
    React.useEffect(() => {
        defaultShapeRef.current = yearFilter === ARCHIVE_YEAR_ALL && !searchQuery.trim() && typeFilter.size === 0;
    }, [yearFilter, searchQuery, typeFilter]);
    React.useLayoutEffect(() => {
        const el = scrollRef.current;
        if (el && savedArchiveScrollTop) el.scrollTop = savedArchiveScrollTop;
        return () => { if (el) savedArchiveScrollTop = defaultShapeRef.current ? el.scrollTop : 0; };
    }, []);

    // TAW-03 (41-09, D-17): merge the TW's own rows with the scoped TAW rows
    // via 41-06's exported helper -- never a second, reimplemented merge.
    // When teamTransactions is empty and includeOwnTransactions is true (the
    // 'own'-scope / no-TAW / solo-agent path -- every existing caller today),
    // mergeScopedTransactions returns `transactions` BY REFERENCE, so this is
    // byte-identical to the pre-plan behavior.
    const scopedTransactions = React.useMemo(
        () => mergeScopedTransactions({ ownTransactions: transactions, teamTransactions, includeOwn: includeOwnTransactions }),
        [transactions, teamTransactions, includeOwnTransactions]
    );

    // 55-20: show and search a contactId-only client by the contact's current
    // name. The contacts lease opens only when such a deal exists.
    const contactNameById = useContactNamesForDeals({
        transactions: scopedTransactions,
        workspaceId
    });

    // D-14: TAW rows on the archive page are a strict VIEWER, mirroring
    // 41-06 Task 3's identical guard on the active list. A row carrying
    // _workspaceId must never enter selectedIds, because handleBulkMove
    // writes to the VIEWING workspace's own transactions collection, and a
    // cross-workspace write triggered from this page is exactly what D-14
    // forbids.
    const foreignRowIds = React.useMemo(
        () => new Set(scopedTransactions.filter(t => t._workspaceId).map(t => t.id)),
        [scopedTransactions]
    );

    const toggleSelected = React.useCallback((id) => {
        if (foreignRowIds.has(id)) return;
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, [foreignRowIds]);

    const selectAllInChapter = React.useCallback((txs) => {
        const selectableTxs = txs.filter(t => !foreignRowIds.has(t.id));
        setSelectedIds(prev => {
            const next = new Set(prev);
            const allSelected = selectableTxs.every(t => next.has(t.id));
            if (allSelected) selectableTxs.forEach(t => next.delete(t.id));
            else selectableTxs.forEach(t => next.add(t.id));
            return next;
        });
    }, [foreignRowIds]);

    const clearSelection = React.useCallback(() => setSelectedIds(new Set()), []);

    const handleBulkMove = React.useCallback(async () => {
        if (!selectedIds.size || !bulkTargetStatus) return;
        setBulkBusy(true);
        try {
            await onBulkMove(Array.from(selectedIds), bulkTargetStatus);
            setSelectedIds(new Set());
        } finally {
            setBulkBusy(false);
        }
    }, [selectedIds, bulkTargetStatus, onBulkMove]);

    // Default bulk target — first status (so users can pull items back to
    // active). D-07 (39-15): manual archive is gone, so every workspace
    // status is a valid bulk-move target now.
    React.useEffect(() => {
        if (statuses.length && !bulkTargetStatus) {
            setBulkTargetStatus(statuses[0] || '');
        }
    }, [statuses, bulkTargetStatus]);

    // D-18: the archive year selector stays the single source of truth --
    // filteredTransactions (fed into the rendered chapters below) filters
    // scopedTransactions, not the raw own-only `transactions` prop, so the
    // selected year's rows on screen include merged TAW rows too. TAW rows
    // are already server-windowed to the selected year (41-05); the
    // deriveArchiveYear check below is a no-op for them and defense in depth
    // if a row ever carries a mismatched year.
    const filteredTransactions = React.useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const hasType = typeFilter.size > 0;
        const target = yearFilter === ARCHIVE_YEAR_ALL ? null : Number(yearFilter);
        return scopedTransactions.filter(t => {
            if (target !== null && deriveArchiveYear(t) !== target) return false;
            if (hasType && !typeFilter.has(t.transactionType)) return false;
            if (q && !matchesTransactionQuery(t, q, contactNameById)) return false;
            return true;
        });
    }, [scopedTransactions, yearFilter, searchQuery, typeFilter, contactNameById]);

    const { archivedChapters } = useTransactionChapters({
        transactions: filteredTransactions,
        statuses,
        selectedIds,
        currentYear,
        workspaceId
    });

    // Single pass: count transactions per year, from the MERGED set, so the
    // selected year's count matches the rows actually on screen (D-18). In
    // the no-team case mergeScopedTransactions returns `transactions` by
    // reference, so this memo's inputs -- and therefore these labels -- are
    // byte-identical to before this plan. The memo invalidates only when the
    // scopedTransactions array identity changes, so the O(N) walk runs at
    // most once per page-data update — not every render.
    const yearCounts = React.useMemo(() => {
        const counts = new Map();
        scopedTransactions.forEach(t => {
            const y = deriveArchiveYear(t);
            if (y) counts.set(y, (counts.get(y) || 0) + 1);
        });
        return counts;
    }, [scopedTransactions]);

    // Own-workspace archive years are ALWAYS fully known, independent of
    // teamScope -- `transactions` (App.js's allTransactions) is the whole,
    // unfiltered own set, never year-windowed. This is what makes the year
    // RAIL'S OPTION SET below independent of the active team scope, fixing
    // the defect where selecting a specific-TAW chip (which flips
    // includeOwnTransactions to false, collapsing scopedTransactions to
    // team-only) made every own-workspace year other than the reachability
    // floor disappear from the rail.
    const ownYearCounts = React.useMemo(() => {
        const counts = new Map();
        transactions.forEach(t => {
            const y = deriveArchiveYear(t);
            if (y) counts.set(y, (counts.get(y) || 0) + 1);
        });
        return counts;
    }, [transactions]);

    const yearTabs = React.useMemo(() => {
        const tabs = [];
        // 'All years' is only ever offered under teamScope === 'own'. A
        // team-agent archive slice loads exactly one year at a time (D-18's
        // bounded, one-shot getDocs per (taw, year)), so 'All years' paired
        // with any team scope can only ever silently degrade to "your own
        // rows only" -- the exact confusing state an operator flagged
        // (previously papered over with an apologetic explanatory
        // paragraph). Omitting the option here, together with the
        // auto-redirect effect below that corrects yearFilter the instant a
        // team scope becomes active while parked on 'All years', PREVENTS
        // that state rather than explaining it after the fact.
        if (teamScope === 'own') {
            tabs.push({ value: ARCHIVE_YEAR_ALL, label: `All years (${scopedTransactions.length})` });
        }
        // Year OPTIONS (which tabs exist) come from the always-available own
        // set (ownYearCounts) UNIONED with whatever team rows are currently
        // loaded (scopedTransactions) -- never from scopedTransactions
        // alone. scopedTransactions only ever carries team rows for the one
        // (taw|all, year) slice useTawTransactions has actually fetched
        // (D-17/D-18's bounded, one-shot getDocs per selected year), so
        // deriving the OPTION SET from it collapsed every other year --
        // including years the own workspace demonstrably has archived deals
        // in -- the instant a TAW chip made includeOwnTransactions false.
        //
        // Every candidate year is also gated to `y < currentYear`
        // (archiveGrouping.isArchivedDeal's own rule): a Sold/Terminated
        // deal whose derived year is the current year (or later -- a data
        // anomaly) is NOT archived by definition and can never render
        // inside archivedChapters, regardless of source (own rows, TAW
        // rows, or the reachability floor below). Offering that year as a
        // tab would print a count for rows the list will always filter out
        // -- a tab whose count disagrees with what actually renders once
        // picked, the exact bug class this project has already shipped
        // three times.
        const years = new Set();
        ownYearCounts.forEach((_, y) => { if (y < currentYear) years.add(y); });
        scopedTransactions.forEach(t => {
            const y = deriveArchiveYear(t);
            if (y != null && y < currentYear) years.add(y);
        });
        // Reachability floor (D-18): unchanged bound of at most 2 extra
        // tabs, but shifted to PAST years (currentYear - 1, currentYear - 2)
        // rather than (currentYear, currentYear - 1) -- currentYear itself
        // can never hold an archived deal (see the guard above), so it must
        // never be offered regardless of source. Without this floor, a year
        // in which only a TAW closed a deal (no own-workspace deal that
        // year) would still be unselectable. Enumerating a TAW's own
        // archived years would require the unbounded all-archived-years-
        // per-TAW query D-18 rejected. Older TAW-only years remain
        // unreachable -- an accepted, documented limitation, not an
        // oversight.
        if (isParentWorkspace && childWorkspaces.length > 0) {
            years.add(currentYear - 1);
            years.add(currentYear - 2);
        }

        // Counts stay scope-honest: a printed count must match what will
        // actually render if that tab is picked (D-18's own standard,
        // applied here to every tab, not just the active one). That holds
        // in exactly two cases: (1) this IS the currently active year, so
        // scopedTransactions already reflects its rows under the current
        // scope; or (2) the current scope excludes team rows entirely
        // ('own'), in which case the own-only count is complete for EVERY
        // year, not only the loaded one. Any other combination -- a
        // specific TAW or 'all' scope, for a year other than the one
        // currently loaded -- cannot be counted without querying that
        // year's team data, which D-18 forbids prefetching. Printing "(0)"
        // there would assert "this agent closed nothing that year", which
        // is worse than printing nothing, so the count is OMITTED rather
        // than guessed.
        const activeYear = yearFilter === ARCHIVE_YEAR_ALL ? null : Number(yearFilter);
        Array.from(years).sort((a, b) => b - a).forEach(y => {
            const countKnown = y === activeYear || teamScope === 'own';
            const label = countKnown ? `${y} (${yearCounts.get(y) || 0})` : `${y}`;
            tabs.push({ value: String(y), label });
        });
        return tabs;
    }, [scopedTransactions, yearCounts, ownYearCounts, isParentWorkspace, childWorkspaces.length, currentYear, yearFilter, teamScope]);

    // Prevent, not explain (replaces the removed apologetic all-years
    // notice paragraph the operator flagged as an apology for confusing
    // behavior rather than a fix for it): the instant the rail would
    // otherwise sit on 'All years' while a team scope is active -- reached
    // by picking a team scope from 'own' while already parked on 'All
    // years' -- redirect straight to the most recent SELECTABLE year
    // instead. yearTabs omits 'All years' under any non-'own' scope (see
    // above) and is sorted year-descending, so its first entry here is
    // exactly the most recent past year with an offered tab; the D-25 gate
    // that must hold before teamScope can leave 'own' (isParentWorkspace &&
    // childWorkspaces.length > 0) is the same gate feeding the reachability
    // floor, so yearTabs is guaranteed non-empty whenever this fires.
    React.useEffect(() => {
        if (teamScope === 'own' || yearFilter !== ARCHIVE_YEAR_ALL) return;
        const mostRecentYear = yearTabs[0];
        if (mostRecentYear) handleYearFilterChange(mostRecentYear.value);
    }, [teamScope, yearFilter, yearTabs, handleYearFilterChange]);

    // Build distribution for the bulk bar's "1 Terminated, 2 Withdrawn" text
    const distribution = React.useMemo(() => {
        const dist = {};
        const txById = new Map(filteredTransactions.map(t => [t.id, t]));
        selectedIds.forEach(id => {
            const t = txById.get(id);
            if (!t) return;
            const status = (t.status || '').trim() || 'Uncategorized';
            dist[status] = (dist[status] || 0) + 1;
        });
        return dist;
    }, [selectedIds, filteredTransactions]);

    const archivedIds = React.useMemo(
        () => archivedChapters.map(c => c.id),
        [archivedChapters]
    );
    const activeIdInView = useChapterScrollSpy(archivedIds);

    const toggleChapterCollapsed = (id, defaultCollapsed) => {
        setChapterCollapseOverrides(prev => {
            const next = new Map(prev);
            const current = next.has(id) ? next.get(id) : defaultCollapsed;
            const flipped = !current;
            // If the flipped value equals the default, drop the override to keep state clean.
            if (flipped === defaultCollapsed) next.delete(id);
            else next.set(id, flipped);
            return next;
        });
    };

    return (
        <ContactNamesContext.Provider value={contactNameById}>
        <div ref={scrollRef}className="bg-[color:var(--body-bg)] h-full overflow-y-auto" style={{ scrollPaddingTop: '8px' }}>
            <main className="max-w-[1320px] mx-auto px-4 md:px-8 xl:pr-16 pt-6 pb-[40vh] space-y-6">
                <header className="space-y-5">
                    {/* Mirrors the Active list header (TransactionListV2): title on the
                        left, the Active | Archive toggle top-right — so neither the toggle
                        nor the heading jumps position when switching between the two pages. */}
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div id="top">
                            <h1 className="text-2xl font-semibold">Archive</h1>
                            <p className="text-base mt-1 text-[color:var(--text-muted)]">
                                {archivedChapters.reduce((n, c) => n + c.count, 0)} transactions across {archivedChapters.length} archived {archivedChapters.length === 1 ? 'status' : 'statuses'}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <TransactionViewTabs active="archive" onSelectActive={onBack} onSelectArchive={() => {}} />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-[11px] uppercase tracking-[.14em] text-[color:var(--text-muted)]">Year</span>
                        <PillTabRail
                            tabs={yearTabs}
                            activeValue={yearFilter}
                            onChange={handleYearFilterChange}
                            ariaLabel="Filter by year"
                        />
                    </div>

                    {/* Search + client-type quick filter, mirroring the Active list. */}
                    <div className="relative" style={{ maxWidth: '420px' }}>
                        <Search
                            size={17}
                            aria-hidden="true"
                            className="pointer-events-none absolute"
                            style={{ color: 'var(--text-muted)', left: '13px', top: '50%', transform: 'translateY(-50%)' }}
                        />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by property, client, or status…"
                            aria-label="Search archived transactions"
                            className="w-full text-sm focus:outline-none"
                            style={{
                                backgroundColor: 'var(--surface-primary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '10px',
                                padding: '10px 13px 10px 38px',
                                color: 'var(--text-primary)'
                            }}
                        />
                    </div>
                    <TransactionTypeFilter active={typeFilter} onToggle={toggleTypeFilter} />

                    {/* TAW-03 (41-09, D-25): only renders in a TW with at least one
                        TAW, never a one-option row -- the verbatim gate expression
                        41-06 Task 3 uses at the active-list mount site. */}
                    {isParentWorkspace && childWorkspaces.length > 0 && (
                        <TeamScopeFilter value={teamScope} taws={childWorkspaces} onChange={onScopeChange} />
                    )}
                </header>

                {archivedChapters.map(chapter => {
                    // When any filter is active, a chapter with zero matching rows
                    // drops out entirely (matching the Active list's search behavior)
                    // rather than showing an empty collapsed header.
                    if ((searchQuery.trim() !== '' || typeFilter.size > 0) && chapter.count === 0) return null;
                    const defaultCollapsed = chapter.count === 0;
                    const isCollapsed = chapterCollapseOverrides.has(chapter.id)
                        ? chapterCollapseOverrides.get(chapter.id)
                        : defaultCollapsed;
                    const kind = statusKindFor(chapter.status);
                    return (
                        <div key={chapter.id} id={`chapter-block-${chapter.id}`}>
                            {/* Phase 37 Plan 05: StatusChapter lost its children/body slot when
                                it became TransactionListV2's GroupedVirtuoso groupContent header
                                (collapse is now row-omission upstream, not a hidden children
                                slot). This page is NOT virtualized — it renders ChapterTable as a
                                plain sibling below the header instead, gated on isCollapsed here
                                (the same gate StatusChapter used to own internally). Neither
                                rowCount nor renderStrip is passed, so StatusChapter's own
                                (virtualization-only) column-header/strip rendering stays off and
                                ChapterTable keeps rendering its own internal header, unchanged. */}
                            {/* This page is not virtualized, so it doesn't get
                                GroupedVirtuoso's sticky group headers for free — wrap the
                                header so it pins to the top of the scroll container while
                                its chapter's rows scroll past (matching the Active list).
                                StatusChapter's section is already opaque, so rows don't
                                show through. */}
                            <div className="sticky top-0 z-20">
                                <StatusChapter
                                    id={chapter.id}
                                    title={chapter.status}
                                    statusKind={kind}
                                    isCollapsed={isCollapsed}
                                    onToggleCollapsed={() => toggleChapterCollapsed(chapter.id, defaultCollapsed)}
                                    metric={<>{chapter.count} deals</>}
                                />
                            </div>
                            {!isCollapsed && teamScope !== 'all' && (
                                <ChapterTable
                                    transactions={chapter.transactions}
                                    chapterKind={kind === 'terminated' || kind === 'withdrawn' ? 'sold' : kind}
                                    canViewFinancials={canViewFinancials}
                                    selectedIds={selectedIds}
                                    onToggleSelected={toggleSelected}
                                    onSelectAllInChapter={selectAllInChapter}
                                    onRowClick={onEdit}
                                />
                            )}
                            {/* D-19: attribution as a per-workspace GROUP LABEL, not a
                                per-row badge like the active list's ChapterRow wrapper --
                                ChapterTable.js maps its rows internally and exposes no
                                per-row slot, and it is owned by no plan in this phase, so
                                editing it is outside this plan's fence. The invariant D-19
                                actually asserts (every TAW row shows its agent's workspace
                                name, in All-team mode and only in All-team mode) holds
                                identically on both surfaces either way. */}
                            {!isCollapsed && teamScope === 'all' && (() => {
                                const ownRows = chapter.transactions.filter(t => !t._workspaceId);
                                const foreignRows = chapter.transactions.filter(t => t._workspaceId);
                                const groupsByWorkspace = [];
                                const seenWorkspaceIds = new Set();
                                foreignRows.forEach(t => {
                                    if (seenWorkspaceIds.has(t._workspaceId)) return;
                                    seenWorkspaceIds.add(t._workspaceId);
                                    groupsByWorkspace.push({
                                        workspaceId: t._workspaceId,
                                        workspaceName: t._workspaceName || t._workspaceId,
                                        rows: foreignRows.filter(r => r._workspaceId === t._workspaceId),
                                    });
                                });
                                const sharedProps = {
                                    chapterKind: kind === 'terminated' || kind === 'withdrawn' ? 'sold' : kind,
                                    canViewFinancials,
                                    selectedIds,
                                    onToggleSelected: toggleSelected,
                                    onSelectAllInChapter: selectAllInChapter,
                                    onRowClick: onEdit,
                                };
                                return (
                                    <>
                                        {ownRows.length > 0 && (
                                            <ChapterTable transactions={ownRows} {...sharedProps} />
                                        )}
                                        {groupsByWorkspace.map(group => (
                                            <div key={group.workspaceId}>
                                                <div
                                                    data-testid="taw-row-badge"
                                                    aria-label={`Deals owned by ${group.workspaceName}`}
                                                    className="px-3 pt-1.5 pb-1 text-[11px] font-medium text-[color:var(--text-muted)] border-t border-[color:var(--border-color)]"
                                                >
                                                    {group.workspaceName}
                                                </div>
                                                <ChapterTable transactions={group.rows} {...sharedProps} />
                                            </div>
                                        ))}
                                    </>
                                );
                            })()}
                        </div>
                    );
                })}

                <BulkActionBar
                    selectedCount={selectedIds.size}
                    distribution={distribution}
                    statuses={statuses}
                    bulkTargetStatus={bulkTargetStatus}
                    onTargetChange={setBulkTargetStatus}
                    onApply={handleBulkMove}
                    onClear={clearSelection}
                    isBusy={bulkBusy}
                />
            </main>

            <ChapterRail
                chapters={archivedChapters.map(c => ({ id: c.id, status: c.status, count: c.count, label: c.status }))}
                activeId={activeIdInView}
                ariaLabel="Archived chapters"
                onPillClick={(chapterId) => {
                    // Force-expand the chapter, then scroll to it. This page is NOT
                    // virtualized, so — unlike TransactionListV2's Virtuoso
                    // scrollToIndex — the jump is a DOM scrollIntoView on the chapter's
                    // NON-sticky outer block id (`chapter-block-${id}`), NOT the
                    // StatusChapter <section id={chapter.id}> — the section lives inside
                    // the `sticky top-0` wrapper, so its measured rect reflects the
                    // header's STUCK position and scrollIntoView stops as soon as the
                    // header pins, leaving the rows off-screen. The outer block is not
                    // sticky, so its rect is the chapter's true top. ChapterRail's old
                    // href anchor was dropped in Plan 37-06, so this is now the scroll
                    // path for the archive. requestAnimationFrame waits for the expand
                    // re-render so the target's final position is measured.
                    setChapterCollapseOverrides(prev => {
                        const next = new Map(prev);
                        next.set(chapterId, false);
                        return next;
                    });
                    requestAnimationFrame(() => {
                        document.getElementById(`chapter-block-${chapterId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    });
                }}
            />
        </div>
        </ContactNamesContext.Provider>
    );
};

export default TransactionsArchivePage;
